import { Injectable } from '@angular/core';

// Models
import { MetaData, PaginatedData, ResponseData } from 'src/app/platform/models/utils';
import { PaymentGenerateData } from 'src/app/modules/payments/models/payments';
import { InvoicesData, InvoicesCallsData } from 'src/app/modules/invoices/models/invoices';
import { UserBalance } from 'src/app/modules/account/models/user';

// Services
import { ApiService } from 'src/app/platform/services/api.service';
import { UserService } from 'src/app/modules/account/user.service';
import { PaymentsService } from 'src/app/modules/payments/payments.service';
import { dateFormat, objectToString } from 'src/app/platform/utils/functions';

/**
 * This class handles all actions related to invoices
 */
@Injectable({
  providedIn: 'root'
})
export class InvoicesService {

  constructor(
    private apiService: ApiService,
    private userService: UserService,
    private paymentsService: PaymentsService
  ) { }

  /**
   * Get a list of invoices
   */
  invoicesGet(page?: number | null, limit?: number | null, filters: any | null = null): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      let data: any = {page, limit};

      if (filters !== null) {
        data = {
          page,
          limit,
          tipos: filters.type,
          estados: filters.status,
          fechaEmisionDesde: dateFormat(filters.date.start, 'YYYY/MM/DD 00:00:00'),
          fechaEmisionHasta: dateFormat(filters.date.end, 'YYYY/MM/DD 23:59:59'),
          fechaVencimientoDesde: dateFormat(filters.dateExpiration.start, 'YYYY/MM/DD 00:00:00'),
          fechaVencimientoHasta: dateFormat(filters.dateExpiration.end, 'YYYY/MM/DD 23:59:59'),
        };
      }

      this.apiService.request('get', 'facturas/' + this.userService.currentUser.value.client + objectToString(data), {}, headers).toPromise().then((success: any) => {
        if (success.response.status){
          const invoicesData: Array<InvoicesData> = [];

          success.items.forEach((value: any, index: number) => {
            invoicesData.push(new InvoicesData().configData(value));
          });

          resolve(this.apiService.successDataPrepare('', new PaginatedData(invoicesData, success.totalRegistros, success.limiteRegistros, success.paginas, success.paginaActual)));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Get the last invoice issued
   */
  invoicesLastGet(): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      this.invoicesGet().then((success: any) => {
        success.result = success.result.registers[0];

        resolve(success);
      }, (error) => {
        reject(error);
      });
    });
  }

  /**
   * Generate a payment process from all unpaid invoices
   */
  invoicesPayAll(method: 'mercadopago'|'pagos360', cancelUnsuscribe: boolean = false): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      this.invoicesGet().then((invoicesSuccess) => {
        if (invoicesSuccess.result.registersTotal > 0) {
          const receipts: Array<PaymentGenerateData> = [];

          invoicesSuccess.result.registers.forEach((invoice: InvoicesData) => {
            if (invoice.status === 'Impaga') {
              receipts.push(new PaymentGenerateData(invoice.id, invoice.type, invoice.balance));
            }
          });

          this.paymentsService.paymentGenerate(method, receipts, cancelUnsuscribe).then((success) => {
            resolve(success);
          }).catch((error) => {
            reject(error);
          });
        } else {
          reject(this.apiService.errorDataPrepare('No hay facturas en estado para generar un cobro total.', invoicesSuccess));
        }
      }).catch((invoicesError) => {
        reject(invoicesError);
      });
    });
  }

  /**
   * Get an invoice
   */
  invoiceGet(invoice: number, type: string): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'facturas/' + this.userService.currentUser.value.client + '/' + type + '/' + invoice, {}, headers).toPromise().then((success: any) => {
        if (success.response.status){
          resolve(this.apiService.successDataPrepare('', new InvoicesData().configData(success)));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Obtains a list of types of invoices
   */
  invoicesTypesGet(): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'facturas/' + this.userService.currentUser.value.client + '/tipos', {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const responseData: Array<MetaData> = [];

          success.tipos.forEach((value: any, index: number) => {
            responseData.push(new MetaData(value, value));
          });

          resolve(this.apiService.successDataPrepare('', responseData));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Obtains a list of status of invoices
   */
  invoicesStatusGet(): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'facturas/' + this.userService.currentUser.value.client + '/estados', {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const responseData: Array<MetaData> = [];

          success.estados.forEach((value: any, index: number) => {
            responseData.push(new MetaData(value, value));
          });

          resolve(this.apiService.successDataPrepare('', responseData));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Get a list of calls associated with an invoice
   */
  callsGet(invoiceId: number, invoiceType: string, page?: number | null, limit?: number | null, filters: any | null = null): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      let data: any = {page, limit};

      if (filters !== null) {
        data = {
          page,
          limit,
          tipos: filters.type.join(','),
          destino: filters.destinationName,
          numOrigen: filters.originNumber,
          numDestino: filters.destinationNumber,
          fechaDesde: dateFormat(filters.date.start, 'YYYY/MM/DD 00:00:00'),
          fechaHasta: dateFormat(filters.date.end, 'YYYY/MM/DD 23:59:59'),
        };
      }

      this.apiService.request('get', 'facturas/' + this.userService.currentUser.value.client + '/' + invoiceType + '/' + invoiceId + '/llamadas' + objectToString(data), {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const callsData: Array<InvoicesCallsData> = [];

          success.items.forEach((value: any, index: number) => {
            callsData.push(new InvoicesCallsData().configData(value));
          });

          resolve(this.apiService.successDataPrepare('', new PaginatedData(callsData, success.totalRegistros, success.limiteRegistros, success.paginas, success.paginaActual)));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Obtains a list of types of calls
   */
  callsTypesGet(invoiceId: number, invoiceType: string): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'facturas/' + this.userService.currentUser.value.client + '/' + invoiceType + '/' + invoiceId + '/tipollamadas', {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const responseData: Array<MetaData> = [];

          success.tipos.forEach((value: any, index: number) => {
            responseData.push(new MetaData(value, value));
          });

          resolve(this.apiService.successDataPrepare('', responseData));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Obtains a list of origin numbers
   */
  callsNumbersGet(invoiceId: number, invoiceType: string): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'facturas/' + this.userService.currentUser.value.client + '/' + invoiceType + '/' + invoiceId + '/numorigenllamadas', {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const responseData: Array<MetaData> = [];

          success.numOrigen.forEach((value: any, index: number) => {
            responseData.push(new MetaData(value, value));
          });

          resolve(this.apiService.successDataPrepare('', responseData));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Send an invoice by mail to all the addresses associated with the account
   */
  emailSend(invoiceId: number, invoiceType: string): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('post', 'facturas/' + this.userService.currentUser.value.client + '/' + invoiceType + '/' + invoiceId + '/enviarmail', {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          if (typeof success.id !== 'undefined' && success.id > 0){
            resolve(this.apiService.successDataPrepare('', success));
          } else {
            reject(this.apiService.errorDataPrepare('Hubo un inconveniente al intentar enviar la factura. Estamos trabajando para solucionar el inconveniente.', success));
          }
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Generate and obtain the PDF link of an invoice
   */
  pdfGet(invoiceId: number, invoiceType: string): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'facturas/' + this.userService.currentUser.value.client + '/' + invoiceType + '/' + invoiceId + '/linkpdf', {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          if (typeof success.url !== 'undefined' && success.url !== ''){
            resolve(this.apiService.successDataPrepare('', success));
          } else {
            reject(this.apiService.errorDataPrepare('Hubo un inconveniente al intentar generar el PDF de la factura. Estamos trabajando para solucionar el inconveniente.', success));
          }
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }
}
